import { fold } from '@pos/i18n';

/** What a division list item needs for matching: its name and its translations. */
export interface NamedDivision {
  code: string;
  name: string;
  nameI18n?: Readonly<Record<string, string | undefined>>;
}

// The kind of unit a name starts with, in Vietnamese and English, which providers and the master data write differently
// ("Thành phố Hồ Chí Minh" / "Hồ Chí Minh", "Phường Bến Thành" / "Ben Thanh Ward").
const KINDS =
  /^(thanh pho|tinh|quan|huyen|thi xa|thi tran|phuong|xa|city|province|district|ward|commune|town)\s+|\s+(city|province|district|ward|commune|town)$/g;

/** A name as it compares: accents, case, the kind of unit and punctuation left out. */
export function placeKey(name: string): string {
  let key = fold(name).replace(/[.,]/g, ' ').replace(/\s+/g, ' ').trim();
  for (let i = 0; i < 2; i++) key = key.replace(KINDS, '').trim();
  return key;
}

/** The division whose name (in any language) is the provider's name, else null: a guess is never made. */
export function matchDivision<T extends NamedDivision>(
  name: string | null | undefined,
  divisions: readonly T[],
): T | null {
  if (!name?.trim()) return null;
  const wanted = placeKey(name);
  if (!wanted) return null;
  const names = (d: T) => [d.name, ...Object.values(d.nameI18n ?? {})].filter((n): n is string => !!n);
  return divisions.find((d) => names(d).some((n) => placeKey(n) === wanted)) ?? null;
}
